<div class="shell">
  <header class="page-heading">
    <p class="eyebrow">Daily cost comparison</p>
    <h1>Pet Wants Cincinnati Daily Cost Calculator</h1>
    <p class="page-heading-sub">Fresh · Local · Delivered</p>
  </header>

  <div class="calculator">
    <div class="main-grid">
      <section class="left-panel">
        <h2 class="panel-title">Your current routine</h2>
        <p class="panel-copy">Choose a comparison food and how much you feed per day. The calculator translates that into the Pet Wants equivalent and shows how close the daily price really is.</p>

        <div class="field">
          <label id="foodSelectLabel">Current food</label>
          <mat-form-field class="w-100 normalized-mat-control food-select">
            <mat-select [value]="selectedFoodId()" (selectionChange)="onFoodChange($event.value)"
              panelClass="cost-calculator-select-panel" aria-labelledby="foodSelectLabel">
              @for (food of foods; track food.id) {
                <mat-option [value]="food.id">{{ food.name }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
          <div class="help">The first options use preloaded figures. Choose <strong>Other</strong> to enter your own data.</div>
        </div>

        <div class="manual-fields" [class.is-visible]="isCustomFood()">
          <div class="field">
            <label for="manualName">Food or formula name</label>
            <input id="manualName" type="text" [value]="manualNameRaw()" (input)="onManualNameChange($event)">
          </div>
          <div class="field">
            <label for="manualKcal">Calories per cup</label>
            <input id="manualKcal" type="number" min="1" step="1" [value]="manualKcalRaw()" (input)="onManualKcalChange($event)">
          </div>
          <div class="field">
            <label for="manualCostCup">Cost per cup ($)</label>
            <input id="manualCostCup" type="number" min="0" step="0.01" [value]="manualCostCupRaw()" (input)="onManualCostCupChange($event)">
          </div>
          <div class="field">
            <label for="manualCupsPound">Cups per pound</label>
            <input id="manualCupsPound" type="number" min="0.1" step="0.1" [value]="manualCupsPoundRaw()" (input)="onManualCupsPoundChange($event)">
          </div>
        </div>

        <div class="field">
          <label for="cupsInput">Cups fed per day</label>
          <input id="cupsInput" type="number" min="0.1" step="0.1" [value]="cupsPerDayRaw()" (input)="onCupsChange($event)">
          <div class="quick-cups">
            @for (cups of quickCups; track cups) {
              <button type="button" (click)="setCupsPerDay(cups)">{{ cups }} {{ cups === 1 ? 'cup' : 'cups' }}</button>
            }
          </div>
          <div class="help">Use the total across all meals.</div>
        </div>
      </section>

      <section class="right-panel">
        <div class="eyebrow">Equal-calorie comparison</div>
        <h2 class="panel-title">Here is the Pet Wants equivalent.</h2>

        <div class="price-compare">
          <div class="big-card">
            <div class="card-label">{{ currentFood().name }}</div>
            <div class="big-value">{{ currentDailyCostLabel() }}</div>
            <div class="sub-metric">{{ currentMonthlyEstimate() }}</div>
          </div>
          <div class="big-card petwants">
            <div class="card-label">{{ petWants.name }}</div>
            <div class="big-value">{{ petWantsDailyCostLabel() }}</div>
            <div class="sub-metric">{{ petWantsMonthlyEstimate() }}</div>
          </div>
        </div>

        <div class="banner">
          <strong>{{ difference().headline }}</strong>
          <span>{{ difference().sub }}</span>
        </div>

        <section class="value-module" aria-labelledby="value-module-heading">
          <h3 id="value-module-heading">What the Pet Wants price also includes</h3>
          <p class="module-copy">These comparison points help explain where Pet Wants stands apart, and the calculator can emphasize the ones that are especially relevant to the food you selected.</p>
          <div class="relevance-bar is-visible" aria-live="polite">
            <span class="relevance-chip">Especially relevant</span>
            <span>{{ relevanceText() }}</span>
          </div>

          <div class="value-grid">
            <article class="value-card" data-point="freshness" [class.highlight]="isHighlighted('freshness')">
              <div class="value-card-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3c-3.5 0-6 2.7-6 6.4 0 3.5 2.5 6.3 6 11.1 3.5-4.8 6-7.6 6-11.1C18 5.7 15.5 3 12 3Z"></path><path d="M9 12c2-.1 4.7-1.5 6-4"></path></svg>
              </div>
              <div>
                <strong>Small-batch freshness</strong>
                <p>Pet Wants Cincinnati rotates fresh food every four to six weeks, helping shorten the time from production to bowl.</p>
              </div>
            </article>

            <article class="value-card" data-point="fillers" [class.highlight]="isHighlighted('fillers')">
              <div class="value-card-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M4 7h16"></path><path d="M4 12h16"></path><path d="M4 17h10"></path><path d="M18 17h2"></path></svg>
              </div>
              <div>
                <strong>Filler ingredients</strong>
                <p>{{ fillersText() }}</p>
              </div>
            </article>

            <article class="value-card" data-point="local" [class.highlight]="isHighlighted('local')">
              <div class="value-card-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M3 7h11v8H3z"></path><path d="M14 10h3l3 3v2h-6z"></path><circle cx="7.5" cy="18" r="1.8"></circle><circle cx="17.5" cy="18" r="1.8"></circle><path d="M3 15h2"></path><path d="M20 15h1"></path></svg>
              </div>
              <div>
                <strong>Local convenience</strong>
                <p>Neighborhood delivery can replace a store trip, protect feeding continuity, and support a local Cincinnati business.</p>
              </div>
            </article>

            <article class="value-card" data-point="smallerServing" [class.highlight]="isHighlighted('smallerServing')">
              <div class="value-card-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3c1.8 2.2 2.8 3.7 2.8 5.6 0 1.4-.6 2.4-1.5 3.4 2.2-.5 4.7 1.7 4.7 4.8A6 6 0 0 1 12 23a6 6 0 0 1-6-6.2c0-3.5 2.1-5.3 4.4-7.6.8-.8 1.4-1.7 1.6-2.2Z"></path><path d="M12 13.2c1 1 1.6 1.8 1.6 2.9A2.6 2.6 0 0 1 11 18.7a2.8 2.8 0 0 1-2.6-2.9c0-1.4.9-2.3 2-3.4"></path></svg>
              </div>
              <div>
                <strong>Smaller equal-calorie serving</strong>
                <p>{{ petWants.kcalPerCup }} kcal per cup can reduce the number of cups scooped for many comparisons, even when daily calories stay the same.</p>
              </div>
            </article>
          </div>
        </section>

        <details>
          <summary><span>How the comparison works</span><span>＋</span></summary>
          <div class="details-inner">
            <ul>
              <li><strong>Current daily cost</strong> = current cups per day × current cost per cup.</li>
              <li><strong>Pet Wants daily cost</strong> = (current cups per day × current kcal per cup ÷ {{ petWants.kcalPerCup }}) × {{ '$' }}{{ petWants.costPerCup.toFixed(2) }}.</li>
              <li><strong>Serving change</strong> compares your current cups to the equal-calorie Pet Wants cups.</li>
              <li><strong>Filler ingredients</strong> appear when the prototype comparison data includes filler notes for the selected food.</li>
              <li><strong>Highlighted value cards</strong> change based on the selected food, the presence of noted fillers, the price gap, and the size of the serving reduction.</li>
            </ul>
          </div>
        </details>

        <p class="disclaimer">Estimates are educational comparisons. Prices, formulas, calorie figures, cup yields, and inventory can change. Freshness, ingredient quality, and digestibility messaging should be paired with verified product facts and customer education. Do not change a pet's diet or portion solely from this calculator; transition gradually and consult a veterinarian for individualized feeding guidance.</p>
      </section>
    </div>
  </div>
</div>

<app-footer></app-footer>
